import { useMemo } from 'react';
import type { KeyStage } from '@eduwrite/shared';

/**
 * Stage tokens for age-adaptive rendering across all student-app activities.
 *
 * These are the single source of truth for per-KeyStage typography,
 * image sizing, and touch-target floors. Phase-2 activities
 * (CanvasPromptActivity, InteractiveStoryActivity) are the reference
 * implementations — every core activity now reads from this hook so
 * Reception (EYFS) pre-readers and Year 6 (KS2) fluent readers get
 * visibly different UIs without a god-component.
 *
 * Touch-target floors come from `ui-and-styling.md`:
 *   - EYFS: 72px (Reception, fine-motor developing)
 *   - KS1:  64px
 *   - KS2:  56px  (UI density acceptable — fluent readers)
 *
 * Auto-play rule: **EYFS only**. KS1/KS2 fluent readers find
 * unsolicited audio patronising, so the speaker icon is tap-to-play.
 */
export interface StageTokens {
    /** Key stage that produced these tokens (undefined = default KS1). */
    stage: KeyStage | undefined;
    /** Is this the EYFS/Reception experience? (drives auto-audio, icon-first, larger touch targets) */
    isEYFS: boolean;
    /** Is this the KS2 experience? (drives denser layouts, smaller fonts) */
    isKS2: boolean;
    /** Primary activity-title font size. */
    titleFontSize: number;
    /** Instruction / body-copy font size. */
    bodyFontSize: number;
    /** Line height for body copy. */
    bodyLineHeight: number;
    /** Prompt-box / vocab-chip tertiary text. */
    chipFontSize: number;
    /** Submit-button label font size (always bold). */
    submitLabelFontSize: number;
    /** Stimulus-image target height in dp. */
    imageHeight: number;
    /** Minimum touch-target height for primary action buttons. */
    touchTargetMin: number;
    /** Minimum touch-target height for secondary buttons (Clear / Undo). */
    touchTargetSecondary: number;
    /** Whether AudioPromptButton should auto-play on mount. */
    autoPlayAudio: boolean;
}

export function useStageTokens(keyStage: KeyStage | undefined): StageTokens {
    return useMemo<StageTokens>(() => {
        if (keyStage === 'EYFS') {
            return {
                stage: keyStage,
                isEYFS: true,
                isKS2: false,
                titleFontSize: 26,
                bodyFontSize: 18,
                bodyLineHeight: 28,
                chipFontSize: 16,
                submitLabelFontSize: 26,
                imageHeight: 240,
                touchTargetMin: 72,
                touchTargetSecondary: 64,
                autoPlayAudio: true,
            };
        }
        if (keyStage === 'KS2') {
            return {
                stage: keyStage,
                isEYFS: false,
                isKS2: true,
                titleFontSize: 20,
                bodyFontSize: 15,
                bodyLineHeight: 22,
                chipFontSize: 13,
                submitLabelFontSize: 20,
                imageHeight: 160,
                touchTargetMin: 56,
                touchTargetSecondary: 56,
                autoPlayAudio: false,
            };
        }
        // KS1 / default — matches existing CanvasPromptActivity baseline
        return {
            stage: keyStage,
            isEYFS: false,
            isKS2: false,
            titleFontSize: 22,
            bodyFontSize: 16,
            bodyLineHeight: 24,
            chipFontSize: 14,
            submitLabelFontSize: 22,
            imageHeight: 180,
            touchTargetMin: 64,
            touchTargetSecondary: 56,
            autoPlayAudio: false,
        };
    }, [keyStage]);
}
